import { Button, Cards, SpaceBetween, Badge, CopyToClipboard, Link } from '@cloudscape-design/components';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRumClickEvent } from '../utils/rum';
import { DemoDefinition, DemoStatus } from '../types';
import { DemoContext } from './demo-context';
import DemoStatusIndicator from './DemoStatusIndicator';


export function DemoTable() {
  const navigate = useNavigate();
  const items = useContext(DemoContext);
  const recordRumClick = useRumClickEvent();
  // Sort items by status order
  const sortedItems = [...items].sort((a, b) => {
    const statusOrder = {
      [DemoStatus.production]: 0,
      [DemoStatus.staging]: 1,
      [DemoStatus.comingSoon]: 2,
      [DemoStatus.development]: 3,
    };
    return statusOrder[a.status ?? DemoStatus.production] - statusOrder[b.status ?? DemoStatus.production];
  }).filter((item) => item.deployedToPortal !== false).filter((item) => item.visible !== false);

  return (
    <Cards
      items={sortedItems}
      loading={items.length === 0}
      loadingText='Loading...'
      cardDefinition={{
        header: (item: DemoDefinition) => item.name,
        sections: [
          {
            id: 'image',
            content: (item: DemoDefinition) => {
              if (item.demoDetails?.imageUrl) {
                return <img
                  alt={item.name}
                  src={item.demoDetails?.imageUrl}
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'cover',
                  }}
                />;
              }
              return null;
            },
          },
          {
            id: 'description',
            content: (item: DemoDefinition) => item.description,
          },
          {
            id: 'buttons',
            content: (item: DemoDefinition) => {
              return (
                <SpaceBetween size="s" direction='horizontal'>
                  <Button
                    variant='primary'
                    disabled={item.status === DemoStatus.comingSoon}
                    // NOTE: Do not encode the path here, as we want it to treat / as / and not %2F
                    onClick={() => {
                      recordRumClick('view_demo', { path: item.path });
                      window.open(`live-demos/${item.path}`, '_blank');
                    }}
                  >
                    {item.status === DemoStatus.comingSoon ? 'Coming Soon' : 'Visit Demo'}
                  </Button>
                  <Button
                    variant="normal"
                    onClick={() => {
                      recordRumClick('view_details', { path: item.path });
                      navigate(`details/${encodeURIComponent(item.path)}`);
                    }}
                  >
                    Details
                  </Button>
                </SpaceBetween>
              );
            },
          },
          {
            id: 'status',
            header: 'Status',
            content: (item: DemoDefinition) => { return item.status ? <DemoStatusIndicator status={item.status} /> : <DemoStatusIndicator status={DemoStatus.production} /> },
          },
          {
            id: 'sfdc-tag-category',
            content: (item: DemoDefinition) => {
              if (item.demoDetails?.sfdcTagCategory) {
                return (
                  <SpaceBetween direction='vertical' size='xxxs'>
                    <span style={{ fontWeight: 'bold' }}>SFDC Tag Category</span>
                    <Badge color="blue">{item.demoDetails.sfdcTagCategory}</Badge>
                  </SpaceBetween>
                );
              }
              return null;
            }
          },
          {
            id: 'sfdc-tag',
            content: (item: DemoDefinition) => {
              if (item.demoDetails?.sfdcTagCategory && item.demoDetails?.sfdcTagName) {
                return (
                  <SpaceBetween direction='vertical' size='xxxs'>
                    <SpaceBetween direction='horizontal' size='m'>
                      <span style={{ fontWeight: 'bold' }}>
                        SFDC Tag Name </span>
                        <Link variant='info' href='/sfdc-tagging' >SFDC Tagging Help</Link>
                    </SpaceBetween>
                    <CopyToClipboard variant='inline' copyButtonText={item.demoDetails.sfdcTagName} textToCopy={item.demoDetails.sfdcTagName} copyErrorText='Failed to Copy SFDC Tag' copySuccessText='Copied SFDC Tag' />
                    {/* <Badge color="blue">{item.demoDetails.sfdcTagName}</Badge> */}
                  </SpaceBetween>
                );
              }
              return null;
            },
          },
        ],
      }}
    />
  );
}
